import { useState, useEffect } from 'react';
import axios from 'axios';
import '@/App.css';
import { Toaster, toast } from 'sonner';
import { Login, Register, ForgotPassword, ResetPassword } from '@/components/Auth';
import IdleWarningDialog from '@/components/IdleWarningDialog';
import { useIdleLogout } from '@/hooks/useIdleLogout';
import { Sidebar } from '@/components/Sidebar';
import { Dashboard } from '@/pages/Dashboard';
import { Upload } from '@/pages/Upload';
import { ProcessingResults } from '@/pages/ProcessingResults';
import { ProcessedData } from '@/pages/ProcessedData';
import { Corrections } from '@/pages/Corrections';
import { SupplierNormalization } from '@/pages/SupplierNormalization';
import { MLPerformance } from '@/pages/MLPerformance';
import { MLInsights } from '@/pages/MLInsights';
import { ClassificationRules } from '@/pages/ClassificationRules';
import { AdminPanel } from '@/pages/AdminPanel';
import { SupplierProfiles } from '@/pages/SupplierProfiles';
import { Connectors } from '@/pages/Connectors';
import { CategoryTaxonomy } from '@/pages/CategoryTaxonomy';
import { AuditLog } from '@/pages/AuditLog';
import { ApiKeys } from '@/pages/ApiKeys';
import { Reports } from '@/pages/Reports';
import { PrivacyAndData } from '@/pages/PrivacyAndData';
import { Help } from '@/pages/Help';
import { SuperAdmin } from '@/pages/SuperAdmin';
import { AdminWelcomeBanner } from '@/components/AdminWelcomeBanner';
import { CookieConsent } from '@/components/CookieConsent';
import { TrustScoreCard } from '@/components/TrustScoreCard';

// ─── Cookie-only auth setup + CSRF ────────────────────────────────────────
// Every axios call sends the httpOnly `dr_access_token` cookie automatically.
axios.defaults.withCredentials = true;

// Reads the double-submit CSRF token from the (non-httpOnly) cookie set by
// the backend on /auth/login. Returns '' if missing so the request still
// goes through — the middleware will 403 on state-changing calls with no
// token, and the SPA's boot-time /api/auth/csrf ping ensures one exists.
const _readCsrfCookie = () => {
  const m = typeof document !== 'undefined'
    ? document.cookie.match(/(?:^|;\s*)dr_csrf=([^;]+)/)
    : null;
  return m ? decodeURIComponent(m[1]) : '';
};

const _STATE_CHANGING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

// axios interceptor — attaches X-CSRF-Token to every mutating request.
axios.interceptors.request.use((config) => {
  const method = (config.method || 'get').toUpperCase();
  if (_STATE_CHANGING.has(method)) {
    const csrf = _readCsrfCookie();
    if (csrf) {
      config.headers = { ...(config.headers || {}), 'X-CSRF-Token': csrf };
    }
  }
  return config;
});

// Wrap window.fetch so raw fetch() calls also include credentials AND the
// CSRF token on mutating requests. One wrapper handles all existing pages.
if (typeof window !== 'undefined' && !window.__dr_fetch_patched) {
  const _origFetch = window.fetch.bind(window);
  window.fetch = (input, init = {}) => {
    const method = (init.method || 'GET').toUpperCase();
    const headers = new Headers(init.headers || {});
    if (_STATE_CHANGING.has(method)) {
      const csrf = _readCsrfCookie();
      if (csrf && !headers.has('X-CSRF-Token')) headers.set('X-CSRF-Token', csrf);
    }
    return _origFetch(input, { credentials: 'include', ...init, headers });
  };
  window.__dr_fetch_patched = true;
}
// ──────────────────────────────────────────────────────────────────────────

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  // Initialize auth view from the URL so that email links to
  // `/reset-password?token=...` and `/forgot-password` actually work.
  // Anything else (including the root path) defaults to the login view.
  const _initialAuthView = () => {
    if (typeof window === 'undefined') return 'login';
    const p = window.location.pathname;
    if (p === '/reset-password') return 'reset';
    if (p === '/forgot-password') return 'forgot';
    return 'login';
  };
  const _initialResetToken = () => {
    if (typeof window === 'undefined') return '';
    if (window.location.pathname !== '/reset-password') return '';
    const params = new URLSearchParams(window.location.search);
    return params.get('token') || '';
  };
  const [authView, setAuthView] = useState(_initialAuthView);
  const [resetToken, setResetToken] = useState(_initialResetToken);
  // Impersonation: when super_user acts as another tenant, we stash the
  // original session here so we can restore it on Exit.
  const [impersonation, setImpersonation] = useState(null);

  // Strip the reset token from the URL as soon as we've captured it so the
  // browser history / referer never leaks it to third parties.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.location.pathname === '/reset-password' && window.location.search) {
      window.history.replaceState({}, document.title, '/reset-password');
    }
  }, []);

  useEffect(() => {
    // Cookie is the authoritative source of truth. If the browser has the
    // `dr_access_token` httpOnly cookie, /api/auth/me will succeed and hydrate
    // the user; otherwise we treat this as a signed-out session.
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try { setUser(JSON.parse(storedUser)); } catch {}
    }
    // Make sure a CSRF token exists before any state-changing call fires.
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/csrf`).catch(() => {});

    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(freshUser => {
        setUser(freshUser);
        setToken('cookie'); // sentinel so `!token` guard passes; real auth is the cookie
        localStorage.setItem('user', JSON.stringify(freshUser));
      })
      .catch(() => {
        localStorage.removeItem('user');
        // legacy cleanup in case an old build left a token here
        localStorage.removeItem('token');
        setUser(null);
        setToken(null);
      });
  }, []);

  const handleAuthSuccess = (userData) => {
    setUser(userData);
    setToken('cookie');
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const handleLogout = async () => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`, { method: 'POST' });
    } catch {}
    localStorage.removeItem('user');
    localStorage.removeItem('token'); // legacy cleanup
    setUser(null);
    setToken(null);
    setImpersonation(null);
    setActiveTab('dashboard');
  };

  // Auto-logout after 15 minutes of inactivity. The hook is a no-op when the
  // user is signed out (enabled=false), so it doesn't waste timers on the
  // login screen. Environment override via REACT_APP_IDLE_TIMEOUT_MINUTES
  // in case a tenant needs a stricter or laxer policy.
  const idleMinutes = Number(process.env.REACT_APP_IDLE_TIMEOUT_MINUTES) || 15;
  const idle = useIdleLogout({
    enabled: !!user,
    timeoutMs: idleMinutes * 60 * 1000,
    onLogout: async () => {
      toast.warning(`Signed out after ${idleMinutes} minutes of inactivity`);
      await handleLogout();
    },
  });

  const handleStartImpersonation = async (data) => {
    // data: { token, expires_in_minutes, target_user: {...} }
    // Backend has already swapped the httpOnly cookie to the impersonation
    // JWT during POST /api/super/impersonate/{id}, so /api/auth/me now
    // returns the target user. Just fetch and swap frontend state.
    let impUser;
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`);
      if (!r.ok) throw new Error(`auth/me failed: ${r.status}`);
      impUser = await r.json();
    } catch (e) {
      console.error('Impersonation aborted:', e);
      return;
    }
    setImpersonation({
      originalUser: user,
      startedAt: Date.now(),
      ttlMinutes: data.expires_in_minutes,
    });
    setUser(impUser);
    setActiveTab('dashboard');
  };

  const handleStopImpersonation = async () => {
    if (!impersonation) return;
    // Backend restores the super_user cookie during POST /impersonate/stop
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/super/impersonate/stop`, {
        method: 'POST',
      });
    } catch {}
    setUser(impersonation.originalUser);
    setImpersonation(null);
    setActiveTab('super');
  };

  const handleUploadSuccess = () => {
    setActiveTab('results');
  };

  const _isPasswordResetView = authView === 'reset' || authView === 'forgot';

  if (!user || !token || _isPasswordResetView) {
    return (
      <div className="App">
        <Toaster position="top-right" richColors />
        <CookieConsent />
        {authView === 'forgot' ? (
          <ForgotPassword
            onBack={() => { setAuthView('login'); window.history.replaceState({}, document.title, '/'); }}
          />
        ) : authView === 'reset' ? (
          <ResetPassword
            token={resetToken}
            onBack={() => { setAuthView('login'); setResetToken(''); window.history.replaceState({}, document.title, '/'); }}
            onSuccess={() => { setAuthView('login'); setResetToken(''); window.history.replaceState({}, document.title, '/'); }}
          />
        ) : (
          <Login onSuccess={handleAuthSuccess} onForgotPassword={() => setAuthView('forgot')} />
        )}
      </div>
    );
  }

  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      <IdleWarningDialog
        open={idle.showWarning}
        secondsRemaining={idle.secondsRemaining}
        onStay={idle.stayActive}
        onLogoutNow={handleLogout}
      />
      {impersonation && (
        <div
          data-testid="impersonation-banner"
          className="fixed top-0 left-0 right-0 z-50 bg-purple-600 text-white text-sm py-2 px-4 flex items-center justify-between shadow"
        >
          <div>
            <span className="font-semibold">Impersonating </span>
            <span className="opacity-90">{user?.email}</span>
            <span className="opacity-70 ml-3">
              (session expires in ~{impersonation.ttlMinutes} min)
            </span>
          </div>
          <button
            onClick={handleStopImpersonation}
            data-testid="impersonation-exit-btn"
            className="bg-white/20 hover:bg-white/30 rounded px-3 py-1 text-xs font-semibold"
          >
            Exit impersonation
          </button>
        </div>
      )}
      <div className={`flex h-screen overflow-hidden ${impersonation ? 'pt-9' : ''}`}>
        <Sidebar 
          activeTab={activeTab} 
          setActiveTab={setActiveTab}
          onLogout={handleLogout}
          user={user}
        />
        
        <main className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden">
          <div className="px-6 py-8 md:px-12 md:py-12" data-testid="main-content">
            {activeTab === 'dashboard' && (
              <>
                <AdminWelcomeBanner user={user} onNavigate={setActiveTab} />
                <TrustScoreCard token={token} />
                <div className="mt-6">
                  <Dashboard token={token} />
                </div>
              </>
            )}
            {activeTab === 'upload' && <Upload token={token} onUploadSuccess={handleUploadSuccess} />}
            {activeTab === 'results' && <ProcessingResults token={token} />}
            {activeTab === 'processed-data' && <ProcessedData token={token} />}
            {activeTab === 'corrections' && <Corrections token={token} />}
            {activeTab === 'suppliers' && <SupplierNormalization token={token} />}
            {activeTab === 'rules' && <ClassificationRules token={token} />}
            {activeTab === 'connectors' && <Connectors token={token} />}
            {activeTab === 'taxonomy' && <CategoryTaxonomy token={token} />}

            {activeTab === 'ml' && <MLPerformance token={token} />}
            {activeTab === 'insights' && <MLInsights token={token} />}
            {activeTab === 'enrichment' && <SupplierProfiles token={token} />}
            {activeTab === 'reports' && <Reports token={token} />}
            {activeTab === 'admin' && ['admin', 'super_user'].includes(user?.role) && <AdminPanel token={token} />}
            {activeTab === 'audit' && ['admin', 'super_user'].includes(user?.role) && <AuditLog token={token} />}
            {activeTab === 'apikeys' && ['admin', 'super_user', 'analyst'].includes(user?.role) && <ApiKeys token={token} />}
            {activeTab === 'privacy' && <PrivacyAndData token={token} user={user} />}
            {activeTab === 'help' && <Help />}
            {activeTab === 'super' && user?.role === 'super_user' && (
              <SuperAdmin token={token} onImpersonate={handleStartImpersonation} />
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

export default App;
